import { z } from 'zod';
import { ClaimChecks } from './claims.js';
import { Condition, evaluate } from './conditions.js';
import { SkipGuard } from './routes.js';
/**
 * The question engine's shared definitions: the 24 question types, what each one's settings and answer
 * values look like, display logic, and how each type appears in chat. Pure functions only, so the core,
 * the web app and the studio validate and evaluate questions identically.
 */
export const QUESTION_TYPES = [
    'single_choice', 'multiple_choice', 'boolean', 'numeric', 'currency', 'percentage',
    'date', 'date_range', 'free_text', 'rich_text', 'rating', 'matrix',
    'file_upload', 'image', 'url', 'entity_selector', 'asset_selector', 'employee_count',
    'location', 'table', 'repeating_group', 'api_derived', 'ai_derived', 'system_derived',
]; // prettier-ignore
export const QuestionType = z.enum(QUESTION_TYPES);
/** Where an answer came from. Every stored answer carries one. */
export const PROVENANCE = [
    'USER_DECLARED', 'AI_EXTRACTED', 'DOCUMENT_VERIFIED', 'API_DISCOVERED',
    'SECURITY_SCAN', 'ADMIN_ENTERED', 'EXTERNAL_PROVIDER', 'INFERRED',
]; // prettier-ignore
export const Provenance = z.enum(PROVENANCE);
/** What an answer can trigger. Executed by the rules engine, never by app code. */
export const EFFECT_TYPES = [
    'score_change', 'create_risk', 'create_finding', 'ask_question', 'skip_questions', 'start_workflow',
    'complete_workflow', 'request_evidence', 'create_graph_entity', 'create_relationship', 'create_task',
    'create_reminder', 'send_notification', 'require_human_review', 'invoke_ai', 'call_external_api',
]; // prettier-ignore
export const Effect = z.strictObject({
    type: z.enum(EFFECT_TYPES),
    params: z.record(z.string(), z.unknown()).default({}),
});
const Key = z.string().regex(/^[a-z0-9][a-z0-9_]*$/, 'lowercase letters, digits and _');
const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(s)), 'not a real date');
export const Option = z.strictObject({
    value: Key,
    label: z.string().min(1).max(200),
    effects: z.array(Effect).default([]),
});
export const QuestionMetadata = z.strictObject({
    risk_domain: z.string().max(10).optional(),
    controls: z.array(z.string()).default([]),
    frameworks: z.array(z.string()).default([]),
    score_mapping: z.string().max(200).optional(),
    evidence: z.array(z.string()).default([]),
    graph_mapping: z.string().max(200).optional(),
    /** A kill-switch question caps the score. In chat it always needs an explicit tap. */
    kill_switch: z.boolean().default(false),
    /** Below this, the chatbot asks a clarifying question instead of proposing an answer. */
    chat_min_confidence: z.number().min(0).max(1).default(0.75),
    /**
     * A follow-up probe (task 86): the question whose weak answer opens this one. Asked straight after it,
     * ahead of the stage's other questions.
     */
    probe_of: z.string().min(1).max(64).optional(),
});
const Autofill = z.strictObject({
    source: z.enum(['document', 'integration', 'registry', 'scan', 'twin']),
    path: z.string().min(1),
});
const Common = {
    text: z.string().trim().min(1).max(500),
    help: z.string().max(1000).default(''),
    mandatory: z.boolean().default(false),
    /** Shown only when this holds. Omitted means always. */
    visibility: Condition.optional(),
    /**
     * Asked only when an insurer form the assessment prepares reads it (task 96): building a bundle adds the
     * forms' need to `visibility` (`compileForms`), so changing a form in the studio changes who is asked.
     */
    for_forms: z.boolean().optional(),
    /** Asked only after these questions are answered (and hidden when any of them is hidden). */
    depends_on: z.array(z.string().min(1)).default([]),
    /** Effects for value answers (dates, numbers, files); choice answers carry effects on each option. */
    effects: z.array(Effect).default([]),
    autofill: z.array(Autofill).default([]),
    ai_instruction: z.string().max(1000).default(''),
    accepted_provenance: z.array(Provenance).min(1).optional(),
    /** Checks of the answer against the business's documents (task 62). Omitted: none. */
    checks: ClaimChecks.optional(),
    metadata: QuestionMetadata.default(() => QuestionMetadata.parse({})),
    /**
     * The routes that can skip this question (task 85). Compiled from the stages' routes when a bundle is
     * built (`compileFlow`); anything authored here is replaced.
     */
    skipped_by: z.array(SkipGuard).max(100).optional(),
};
const Column = z.strictObject({
    key: Key,
    label: z.string().min(1),
    type: z.enum(['text', 'number', 'date', 'boolean', 'choice']),
    options: z.array(z.string()).optional(),
    required: z.boolean().default(false),
});
const Bounds = { min: z.number().optional(), max: z.number().optional() };
const Files = (accept) => z.strictObject({
    accept: z.array(z.string()).default(accept),
    max_files: z.number().int().min(1).max(20).default(5),
    document_types: z.array(z.string()).default([]),
});
/** Which types need options (choices), may have them (selectors, derived), or must not (value types). */
const OPTION_POLICY = {};
/** Whether a question type needs options (choices), may have them, or must not, for editors. */
export function optionPolicy(type) {
    return OPTION_POLICY[type] ?? 'none';
}
const def = (type, settings, options) => {
    OPTION_POLICY[type] = options;
    return z.strictObject({
        ...Common,
        type: z.literal(type),
        settings: settings,
        options: z.array(Option).default([]),
    });
};
const none = z.strictObject({}).default({});
export const QuestionDefinition = z
    .discriminatedUnion('type', [
    def('single_choice', none, 'required'),
    def('multiple_choice', z
        .strictObject({
        min_selected: z.number().int().min(0).optional(),
        max_selected: z.number().int().min(1).optional(),
    })
        .default({}), 'required'),
    def('boolean', z.strictObject({ allow_not_sure: z.boolean().default(false) }).default({ allow_not_sure: false }), 'required'),
    def('numeric', z
        .strictObject({ ...Bounds, integer: z.boolean().default(false), unit: z.string().optional() })
        .default({ integer: false }), 'none'),
    def('currency', z
        .strictObject({ ...Bounds, currency: z.string().length(3).default('MYR') })
        .default({ currency: 'MYR' }), 'none'),
    def('percentage', z
        .strictObject({ min: z.number().default(0), max: z.number().default(100) })
        .default({ min: 0, max: 100 }), 'none'),
    def('date', z
        .strictObject({
        min: IsoDate.optional(),
        max: IsoDate.optional(),
        not_in_future: z.boolean().default(false),
    })
        .default({ not_in_future: false }), 'none'),
    def('date_range', z.strictObject({ min: IsoDate.optional(), max: IsoDate.optional() }).default({}), 'none'),
    def('free_text', z
        .strictObject({
        max_length: z.number().int().min(1).max(5000).default(1000),
        multiline: z.boolean().default(false),
    })
        .default({ max_length: 1000, multiline: false }), 'none'),
    def('rich_text', z
        .strictObject({ max_length: z.number().int().min(1).max(50000).default(10000) })
        .default({ max_length: 10000 }), 'none'),
    def('rating', z
        .strictObject({
        scale: z.number().int().min(3).max(10).default(5),
        low_label: z.string().optional(),
        high_label: z.string().optional(),
    })
        .default({ scale: 5 }), 'none'),
    def('matrix', z.strictObject({
        rows: z.array(z.strictObject({ key: Key, label: z.string().min(1) })).min(1),
        columns: z.array(z.strictObject({ value: Key, label: z.string().min(1) })).min(2),
        multiple: z.boolean().default(false),
    }), 'none'),
    def('file_upload', Files(['application/pdf', 'image/*']).default(() => Files(['application/pdf', 'image/*']).parse({})), 'none'),
    def('image', Files(['image/*']).default(() => Files(['image/*']).parse({})), 'none'),
    def('url', z.strictObject({ https_only: z.boolean().default(false) }).default({ https_only: false }), 'none'),
    def('entity_selector', z.strictObject({ entity_type: z.string().min(1), multiple: z.boolean().default(false) }), 'optional'),
    def('asset_selector', z
        .strictObject({
        asset_type: z.string().optional(),
        min_selected: z.number().int().min(0).optional(),
        max_selected: z.number().int().min(1).optional(),
    })
        .default({}), 'optional'),
    def('employee_count', z.strictObject({ max: z.number().int().min(1).default(1_000_000) }).default({ max: 1_000_000 }), 'none'),
    def('location', z
        .strictObject({ granularity: z.enum(['country', 'state', 'city', 'address']).default('state') })
        .default({ granularity: 'state' }), 'none'),
    def('table', z.strictObject({
        columns: z.array(Column).min(1),
        min_rows: z.number().int().min(0).default(0),
        max_rows: z.number().int().min(1).default(100),
    }), 'none'),
    def('repeating_group', z.strictObject({
        fields: z.array(Column).min(1),
        item_label: z.string().default('Item'),
        min_items: z.number().int().min(0).default(0),
        max_items: z.number().int().min(1).default(50),
    }), 'none'),
    def('api_derived', z.strictObject({ connector: z.string().min(1), path: z.string().min(1) }), 'optional'),
    def('ai_derived', z.strictObject({ instruction: z.string().min(1), inputs: z.array(z.string()).default([]) }), 'optional'),
    def('system_derived', z.strictObject({ source: z.string().min(1) }), 'optional'),
])
    .superRefine((q, ctx) => {
    const policy = OPTION_POLICY[q.type];
    if (policy === 'required' && q.options.length < 2)
        ctx.addIssue({ code: 'custom', message: 'needs at least 2 options', path: ['options'] });
    if (policy === 'none' && q.options.length)
        ctx.addIssue({ code: 'custom', message: `${q.type} questions take no options`, path: ['options'] });
    const seen = new Set();
    q.options.forEach((o, i) => {
        if (seen.has(o.value))
            ctx.addIssue({
                code: 'custom',
                message: `duplicate option value "${o.value}"`,
                path: ['options', i, 'value'],
            });
        seen.add(o.value);
    });
    if (q.type === 'boolean') {
        const allowed = new Set(['yes', 'no', ...(q.settings.allow_not_sure ? ['not_sure'] : [])]);
        const values = q.options.map((o) => o.value);
        if (!values.includes('yes') || !values.includes('no') || values.some((v) => !allowed.has(v))) {
            ctx.addIssue({
                code: 'custom',
                message: `boolean options must be yes and no${q.settings.allow_not_sure ? ' (and not_sure)' : ''}`,
                path: ['options'],
            });
        }
    }
    if ('min' in q.settings &&
        'max' in q.settings &&
        q.settings.min != null &&
        q.settings.max != null &&
        q.settings.min > q.settings.max) {
        ctx.addIssue({ code: 'custom', message: 'min is greater than max', path: ['settings', 'min'] });
    }
    if (q.depends_on.length !== new Set(q.depends_on).size)
        ctx.addIssue({ code: 'custom', message: 'depends_on lists a question twice', path: ['depends_on'] });
});
export const DERIVED_TYPES = new Set([
    'api_derived',
    'ai_derived',
    'system_derived',
]);
export function acceptedProvenance(q) {
    if (q.accepted_provenance)
        return q.accepted_provenance;
    if (q.type === 'api_derived')
        return ['API_DISCOVERED', 'ADMIN_ENTERED'];
    if (q.type === 'ai_derived')
        return ['AI_EXTRACTED', 'INFERRED', 'ADMIN_ENTERED'];
    if (q.type === 'system_derived')
        return ['INFERRED', 'EXTERNAL_PROVIDER', 'SECURITY_SCAN', 'ADMIN_ENTERED'];
    return [...PROVENANCE];
}
// ---------- answer values ----------
const cell = (c) => {
    const base = c.type === 'number'
        ? z.number()
        : c.type === 'date'
            ? IsoDate
            : c.type === 'boolean'
                ? z.boolean()
                : c.type === 'choice'
                    ? z.enum((c.options ?? ['']))
                    : z.string().max(1000);
    return c.required ? base : base.nullable().optional();
};
const rows = (cols) => z.strictObject(Object.fromEntries(cols.map((c) => [c.key, cell(c)])));
const optionValue = (q) => z.enum(q.options.map((o) => o.value));
const limitCount = (s, min, max) => s
    .refine((a) => min == null || a.length >= min, `pick at least ${min}`)
    .refine((a) => max == null || a.length <= max, `pick at most ${max}`);
const bounded = (s, min, max) => (min != null ? s.min(min) : s).max(max ?? Number.MAX_SAFE_INTEGER);
const FileRefs = (max) => z.strictObject({ document_ids: z.array(z.string().min(1)).min(1).max(max) });
/** The schema an answer value must satisfy for this question. */
export function answerSchema(q) {
    switch (q.type) {
        case 'single_choice':
        case 'boolean':
            return optionValue(q);
        case 'multiple_choice':
            return limitCount(z.array(optionValue(q)).min(1), q.settings.min_selected, q.settings.max_selected).refine((a) => new Set(a).size === a.length, 'an option is picked twice');
        case 'numeric': {
            const n = bounded(z.number(), q.settings.min, q.settings.max);
            return q.settings.integer ? n.int() : n;
        }
        case 'currency':
            return z.strictObject({
                amount: bounded(z.number().nonnegative(), q.settings.min, q.settings.max),
                currency: z.literal(q.settings.currency),
            });
        case 'percentage':
            return z.number().min(q.settings.min).max(q.settings.max);
        case 'employee_count':
            return z.number().int().min(0).max(q.settings.max);
        case 'rating':
            return z.number().int().min(1).max(q.settings.scale);
        case 'date': {
            const { min, max, not_in_future } = q.settings;
            return IsoDate.refine((d) => !min || d >= min, `on or after ${min}`)
                .refine((d) => !max || d <= max, `on or before ${max}`)
                .refine((d) => !not_in_future || d <= new Date().toISOString().slice(0, 10), 'cannot be in the future');
        }
        case 'date_range': {
            const { min, max } = q.settings;
            return z
                .strictObject({ from: IsoDate, to: IsoDate })
                .refine((r) => r.from <= r.to, 'from is after to')
                .refine((r) => (!min || r.from >= min) && (!max || r.to <= max), 'outside the allowed dates');
        }
        case 'free_text':
        case 'rich_text':
            return z.string().trim().min(1).max(q.settings.max_length);
        case 'url':
            return z.url({ protocol: q.settings.https_only ? /^https$/ : /^https?$/ });
        case 'location':
            return z.strictObject({ country: z.string().length(2), state: z.string().optional(), city: z.string().optional(), address: z.string().optional() })
                .refine((l) => q.settings.granularity === 'country' || !!l.state, 'state is required')
                .refine((l) => !['city', 'address'].includes(q.settings.granularity) || !!l.city, 'city is required')
                .refine((l) => q.settings.granularity !== 'address' || !!l.address, 'address is required'); // prettier-ignore
        case 'matrix': {
            const col = z.enum(q.settings.columns.map((c) => c.value));
            const v = q.settings.multiple ? z.array(col).min(1) : col;
            return z.strictObject(Object.fromEntries(q.settings.rows.map((r) => [r.key, q.mandatory ? v : v.optional()])));
        }
        case 'table':
            return z.array(rows(q.settings.columns)).min(q.settings.min_rows).max(q.settings.max_rows);
        case 'repeating_group':
            return z.array(rows(q.settings.fields)).min(q.settings.min_items).max(q.settings.max_items);
        case 'file_upload':
        case 'image':
            return FileRefs(q.settings.max_files);
        case 'entity_selector': {
            const one = q.options.length ? optionValue(q) : z.string().min(1);
            return q.settings.multiple ? z.array(one).min(1) : one;
        }
        case 'asset_selector':
            return limitCount(z.array(q.options.length ? optionValue(q) : z.string().min(1)).min(1), q.settings.min_selected, q.settings.max_selected);
        case 'api_derived':
        case 'ai_derived':
        case 'system_derived':
            return q.options.length ? optionValue(q) : z.json();
    }
}
/** Checks an answer value and where it came from. The answer engine stores only what passes. */
export function checkAnswer(q, value, provenance) {
    if (!acceptedProvenance(q).includes(provenance)) {
        const why = DERIVED_TYPES.has(q.type)
            ? 'this question is answered by the system, not by people'
            : `answers from ${provenance} are not accepted for this question`;
        return { ok: false, errors: [{ path: '', message: why }] };
    }
    const r = answerSchema(q).safeParse(value);
    return r.success
        ? { ok: true, value: r.data }
        : { ok: false, errors: r.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })) };
}
/** The value an implied question counts as, or undefined. */
export const impliedValue = (s) => (s?.state === 'implied' ? s.value : undefined);
/** Answers with implied ones in place of anything stored for those questions. */
export function withImplied(answers, display, implied) {
    const out = { ...answers };
    for (const [k, s] of Object.entries(display))
        if (s.state === 'implied')
            out[k] = implied(s.value);
    return out;
}
/**
 * Works out, for every question, whether it is shown now, waiting on other answers, hidden, or implied
 * by a route. Answers to hidden questions are ignored by other questions' conditions, so changing an
 * earlier answer cleanly hides everything that depended on it; an implied question reads as its implied
 * value. Derived questions are visible but never asked.
 */
export function displayStates(questions, ctx) {
    const out = {};
    const visiting = new Set();
    const effectiveAnswers = new Proxy({}, {
        get: (_, key) => {
            if (typeof key !== 'string' || !(key in questions))
                return ctx.answers[key];
            const s = state(key);
            return s.state === 'hidden' ? undefined : s.state === 'implied' ? s.value : ctx.answers[key];
        },
    });
    const evalCtx = { answers: effectiveAnswers, facts: ctx.facts };
    function state(key) {
        const done = out[key];
        if (done)
            return done;
        if (visiting.has(key))
            return { state: 'hidden', reason: 'cycle', detail: key };
        visiting.add(key);
        const q = questions[key];
        let s = null;
        if (q.visibility && !evaluate(q.visibility, evalCtx))
            s = { state: 'hidden', reason: 'visibility' };
        for (const g of q.skipped_by ?? []) {
            if (s || !evaluate(g.when, evalCtx))
                continue;
            const by = { route: g.route, from: g.from, label: g.label };
            s =
                g.as === 'not_in_place' && g.value !== undefined
                    ? { state: 'implied', value: g.value, by }
                    : { state: 'hidden', reason: 'route', detail: g.from, by };
        }
        if (!s) {
            const waiting = [];
            s = { state: 'visible', ask: !DERIVED_TYPES.has(q.type) };
            for (const dep of q.depends_on) {
                if (!(dep in questions)) {
                    s = { state: 'hidden', reason: 'missing_dependency', detail: dep };
                    break;
                }
                const d = state(dep);
                if (d.state === 'hidden') {
                    s = { state: 'hidden', reason: d.reason === 'cycle' ? 'cycle' : 'dependency_hidden', detail: dep };
                    break;
                }
                if (d.state === 'implied')
                    continue;
                const a = ctx.answers[dep];
                if (d.state === 'waiting' || a === undefined || a === null)
                    waiting.push(dep);
            }
            if (s.state === 'visible' && waiting.length)
                s = { state: 'waiting', on: waiting };
        }
        visiting.delete(key);
        out[key] = s;
        return s;
    }
    for (const key of Object.keys(questions))
        state(key);
    return out;
}
/** How each type appears in the chatbot (architecture doc, "How each question type appears in chat"). */
export const CHAT_PRESENTATION = {
    single_choice: 'quick_replies', multiple_choice: 'quick_replies', boolean: 'quick_replies', rating: 'quick_replies',
    numeric: 'typed', currency: 'typed', percentage: 'typed', employee_count: 'typed', free_text: 'typed', url: 'typed',
    date: 'date_picker', date_range: 'date_picker',
    file_upload: 'upload', image: 'upload',
    rich_text: 'inline_form', matrix: 'inline_form', table: 'inline_form', repeating_group: 'inline_form',
    entity_selector: 'inline_form', asset_selector: 'inline_form', location: 'inline_form',
    api_derived: 'not_asked', ai_derived: 'not_asked', system_derived: 'not_asked',
}; // prettier-ignore
/**
 * The question as the conversation agent sees it, or null when chat shows a form card or upload instead
 * of interpreting free text. Kill-switch questions are still sent: the agent may propose, but the core
 * requires an explicit tap before recording them.
 */
export function toAgentQuestion(key, q) {
    const base = {
        id: key,
        text: q.text,
        kill_switch: q.metadata.kill_switch,
        min_confidence: q.metadata.chat_min_confidence,
        help: q.help,
        instruction: q.ai_instruction,
    };
    const opts = q.options.map((o) => ({ value: o.value, label: o.label }));
    switch (q.type) {
        case 'boolean':
            return q.options.some((o) => o.value === 'not_sure')
                ? { ...base, type: 'single_choice', options: opts }
                : { ...base, type: 'yes_no', options: opts };
        case 'single_choice':
            return { ...base, type: 'single_choice', options: opts };
        case 'multiple_choice':
            return { ...base, type: 'multi_choice', options: opts };
        case 'entity_selector':
        case 'asset_selector':
            // Selectors read like choices when they list their options (more come as a form card).
            if (!opts.length || opts.length > 8)
                return null;
            return q.type === 'asset_selector' || q.settings.multiple
                ? { ...base, type: 'multi_choice', options: opts }
                : { ...base, type: 'single_choice', options: opts };
        case 'rating':
            return {
                ...base,
                type: 'scale',
                options: Array.from({ length: q.settings.scale }, (_, i) => ({
                    value: String(i + 1),
                    label: String(i + 1),
                })),
            };
        case 'numeric':
        case 'currency':
        case 'percentage':
        case 'employee_count':
            return { ...base, type: 'number', options: [] };
        case 'date':
            return { ...base, type: 'date', options: [] };
        case 'free_text':
        case 'url':
            return { ...base, type: 'short_text', options: [] };
        default:
            return null;
    }
}
/** Converts a value the conversation agent proposed into this question's answer shape (then checkAnswer it). */
export function fromAgentValue(q, value) {
    switch (q.type) {
        case 'rating':
        case 'employee_count':
        case 'numeric':
        case 'percentage':
            return typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
        case 'currency':
            return typeof value === 'number' ? { amount: value, currency: q.settings.currency } : value;
        default:
            return value;
    }
}
//# sourceMappingURL=questions.js.map